import React from 'react';
import { AreaChart, Area, ResponsiveContainer, YAxis } from 'recharts';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';
import { Card, CardContent } from './ui/card';

/**
 * KPISparklineCard — vibrant, sparkline-equipped admin KPI tile.
 *
 * Used on /analytics to give Total Events / Unique Users / Sessions /
 * Return Rate the same visual treatment the Cornerstone Trend + Conversion
 * cards already enjoy: gradient + glow border, mini sparkline, week-over-
 * week delta. Replaces the four flat slate cards that used to sit there.
 *
 * Props:
 *   label         display label ("Total Events")
 *   value         current value (already formatted or raw number)
 *   formatValue   optional formatter — e.g. v => `${v}%`
 *   icon          lucide icon component
 *   tone          "emerald" | "blue" | "violet" | "amber"
 *   series        optional array of numbers (oldest → newest) for sparkline + WoW delta
 *   testId        data-testid prefix
 */
const TONE_STYLES = {
  emerald: {
    accent: '#34d399',
    sparkFill: 'rgba(52, 211, 153, 0.25)',
    iconBg: 'bg-emerald-500/15 border-emerald-400/30',
    iconText: 'text-emerald-300',
    glow: 'shadow-[0_0_40px_-15px_rgba(52,211,153,0.45)]',
    border: 'border-emerald-400/25',
    gradient: 'from-slate-900 via-slate-900 to-emerald-950/30',
  },
  blue: {
    accent: '#38bdf8',
    sparkFill: 'rgba(56, 189, 248, 0.25)',
    iconBg: 'bg-sky-500/15 border-sky-400/30',
    iconText: 'text-sky-300',
    glow: 'shadow-[0_0_40px_-15px_rgba(56,189,248,0.45)]',
    border: 'border-sky-400/25',
    gradient: 'from-slate-900 via-slate-900 to-sky-950/30',
  },
  violet: {
    accent: '#22d3ee',
    sparkFill: 'rgba(192, 132, 252, 0.25)',
    iconBg: 'bg-cyan-500/15 border-cyan-400/30',
    iconText: 'text-cyan-300',
    glow: 'shadow-[0_0_40px_-15px_rgba(192,132,252,0.45)]',
    border: 'border-cyan-400/25',
    gradient: 'from-slate-900 via-slate-900 to-cyan-950/30',
  },
  amber: {
    accent: '#fbbf24',
    sparkFill: 'rgba(251, 191, 36, 0.25)',
    iconBg: 'bg-amber-500/15 border-amber-400/30',
    iconText: 'text-amber-300',
    glow: 'shadow-[0_0_40px_-15px_rgba(251,191,36,0.45)]',
    border: 'border-amber-400/25',
    gradient: 'from-slate-900 via-slate-900 to-amber-950/30',
  },
};

const _wow = (series) => {
  if (!Array.isArray(series) || series.length < 8) return null;
  const last7 = series.slice(-7).reduce((s, n) => s + (n || 0), 0);
  const prior7 = series.slice(-14, -7).reduce((s, n) => s + (n || 0), 0);
  if (prior7 === 0) return last7 === 0 ? 0 : 100;
  return Math.round(((last7 - prior7) / prior7) * 100);
};

export const KPISparklineCard = ({
  label,
  value,
  formatValue,
  icon: Icon,
  tone = 'emerald',
  series,
  testId,
}) => {
  const t = TONE_STYLES[tone] || TONE_STYLES.emerald;
  const display = formatValue ? formatValue(value) : (value?.toLocaleString?.() ?? value ?? 0);
  const delta = _wow(series);
  const hasSpark = Array.isArray(series) && series.length >= 2;
  const sparkData = hasSpark ? series.map((v, i) => ({ i, v: v || 0 })) : [];

  let DeltaIcon = Minus;
  let deltaTone = 'text-slate-400 bg-slate-700/40 border-slate-600/40';
  if (delta != null) {
    if (delta > 0) {
      DeltaIcon = TrendingUp;
      deltaTone = 'text-emerald-200 bg-emerald-500/15 border-emerald-400/30';
    } else if (delta < 0) {
      DeltaIcon = TrendingDown;
      deltaTone = 'text-rose-200 bg-rose-500/15 border-rose-400/30';
    }
  }

  return (
    <Card
      data-testid={testId || `kpi-card-${tone}`}
      className={`relative overflow-hidden bg-gradient-to-br ${t.gradient} border ${t.border} ${t.glow} transition-shadow hover:shadow-[0_0_55px_-15px_rgba(255,255,255,0.18)]`}
    >
      <CardContent className="p-5">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 mb-2">
              <div
                className={`w-9 h-9 rounded-lg border ${t.iconBg} flex items-center justify-center shrink-0`}
              >
                {Icon && <Icon className={`w-4 h-4 ${t.iconText}`} />}
              </div>
              <span className="text-[11px] font-bold uppercase tracking-[0.18em] text-slate-400">
                {label}
              </span>
            </div>
            <div
              data-testid={testId ? `${testId}-value` : undefined}
              className="text-3xl font-bold text-white tabular-nums tracking-tight"
            >
              {display}
            </div>
            {delta != null && (
              <div
                data-testid={testId ? `${testId}-delta` : undefined}
                className={`mt-2 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md border text-[11px] font-semibold ${deltaTone}`}
              >
                <DeltaIcon className="w-3 h-3" />
                <span className="tabular-nums">
                  {delta > 0 ? '+' : ''}{delta}%
                </span>
                <span className="text-[11px] opacity-70 uppercase tracking-wider ml-0.5">
                  WoW
                </span>
              </div>
            )}
          </div>
          {hasSpark && (
            <div className="w-24 h-14 shrink-0 -mr-1 -my-1 opacity-90">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={sparkData} margin={{ top: 4, right: 0, bottom: 0, left: 0 }}>
                  <defs>
                    <linearGradient id={`spark-${tone}-${label.replace(/\s/g, '')}`} x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={t.accent} stopOpacity={0.55} />
                      <stop offset="100%" stopColor={t.accent} stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <YAxis hide domain={['dataMin', 'dataMax']} />
                  <Area
                    type="monotone"
                    dataKey="v"
                    stroke={t.accent}
                    strokeWidth={1.6}
                    fill={`url(#spark-${tone}-${label.replace(/\s/g, '')})`}
                    isAnimationActive={false}
                    dot={false}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default KPISparklineCard;
